import { useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactElement } from 'react'
import { Video, VideoOff, Wifi } from 'lucide-react'
import { Badge } from '@/components/play'
import { cameraOrder, type EventPhase } from '@/lib/watch/rules'
import type { PublicProfile, WatchCamera } from '@/lib/watch/types'
import { cn } from '@/lib/utils'
import { HostAvatar } from './HostAvatar'
import { LiveTrack } from './LiveTrack'
import { connectionWait, quietCanvas, quietWords, type CameraCount } from './noCameras'
import { SourceGlyph } from './SourceGlyph'
import { StackedGlyph, StateGlyph } from './StateGlyph'
import type { CanvasConnection } from './WatchCanvas'
import './watch.css'

export interface CameraStripProps {
  cameras: WatchCamera[]
  watchedIdentity: string | null
  calledIdentity: string | null
  host: PublicProfile
  /** An online camera was activated. For a host the page treats it as a preview. */
  onSelect(identity: string): void
  /**
   * `row` scrolls sideways under the canvas; `grid` is the phone's two-column Cameras tab, the Live show and the hosts'
   * screens full width.
   */
  layout?: 'row' | 'grid'
  /**
   * The grid in a narrow side panel (a phone on its side, beside the video): every source takes one cell, the Live show
   * and the hosts' screens too, so the panel's first screen shows cameras rather than one wide tile. The row ignores it.
   */
  compact?: boolean
  /**
   * The event's cameras and Live shows, offline ones included (`live_only` keeps those out of `cameras`). An empty grid
   * says "No cameras here" for none, otherwise that none is live yet, or right now (a show alone, that the Live show
   * isn't), as the canvas. Required: the visible list can't tell an event with none from one whose cameras are hidden.
   */
  cameraCount: CameraCount
  /** Where the event is in its schedule (`before` when left out): an empty grid says what the canvas says. */
  phase?: EventPhase
  /**
   * The room's connection, as the canvas has it. Until it connects (and while it reconnects) nothing can be known about
   * a camera: no tile says Offline and an empty grid waits; `stopped` (the video refused), each tile is crossed out.
   */
  connection: CanvasConnection
  className?: string
}

/** What a tile can say about its camera: playing; on its way (or the room still connecting); offline; or nothing known. */
type TileState = 'live' | 'waiting' | 'offline' | 'unknown'

function tileState(camera: WatchCamera, connection: CanvasConnection): TileState {
  if (connection === 'stopped') return 'unknown'
  if (connection !== 'connected') return 'waiting'
  if (camera.online) return 'live'
  return camera.waiting ? 'waiting' : 'offline'
}

/** A tile's state in its accessible name, after the camera's name (nothing when nothing is known). */
const TILE_WORDS: Record<TileState, string | null> = { live: 'live', waiting: 'connecting', offline: 'offline', unknown: null }

/** Where each state sits in the strip: what plays first, then what is on its way, then the rest. */
const STATE_RANK: Record<TileState, number> = { live: 0, waiting: 1, offline: 2, unknown: 2 }

/**
 * Every camera but the watched one, as buttons in one group: the live ones first, then those on their way, then the
 * offline ones, each in `cameraOrder` (the Live show, then the stage's hosts' shared screens, marked by the screen
 * glyph, then along the course). The canvas shows (and names) the watched
 * camera, so the strip leaves it out and its video never plays twice; when the canvas turns to another camera, the one
 * it left comes back to its place, growing in (the only motion here: the viewer's own doing, or the host's call). The
 * host's call carries the host's avatar, and a camera without a picture shows none and
 * can't be picked: offline (a badge, the crossed-out camera, the tile faded until focused), or on its way (the camera,
 * still, no badge; also every camera while the room connects). A camera going live or offline takes its new place at
 * once. Nothing else in the strip moves: many tiles turning or waking at once would be noise
 * beside the canvas. One tab stop; the arrow keys move between tiles (and between rows in the grid). When the focused
 * camera leaves (picked, it goes to the canvas), focus moves to the tile that takes its place (or the one before it, at
 * the end).
 *
 * Nothing to list: the row draws nothing (the canvas above says so); the phone's grid shows the canvas's glyph on the
 * stacked disc, at rest, its words for screen readers only (the canvas has the heading, the motion and the brand mark):
 * no other camera when the canvas has the only one, no cameras here, none live yet or right now when `live_only` hides
 * them (or the scheduled end has passed), or the connection's wait.
 */
export function CameraStrip({
  cameras,
  watchedIdentity,
  calledIdentity,
  host,
  onSelect,
  layout = 'row',
  compact = false,
  cameraCount,
  phase = 'before',
  connection,
  className,
}: CameraStripProps) {
  const ordered = useMemo(
    () =>
      cameras
        .filter((camera) => camera.identity !== watchedIdentity)
        .sort((a, b) => STATE_RANK[tileState(a, connection)] - STATE_RANK[tileState(b, connection)] || cameraOrder(a, b)),
    [cameras, watchedIdentity, connection],
  )
  const [focusedIdentity, setFocusedIdentity] = useState<string | null>(null)
  // The camera the canvas just left, back in the strip: its tile mounts growing in (watch.css), once.
  const [lastWatched, setLastWatched] = useState(watchedIdentity)
  const [returned, setReturned] = useState<string | null>(null)
  if (lastWatched !== watchedIdentity) {
    setLastWatched(watchedIdentity)
    setReturned(lastWatched)
  }

  // A focused tile that unmounts drops focus to the page (React sends no blur for it), so keep it in the strip.
  const groupRef = useRef<HTMLDivElement>(null)
  const focusInside = useRef(false)
  const previous = useRef(ordered)
  useLayoutEffect(() => {
    const before = previous.current
    previous.current = ordered
    if (!focusInside.current || focusedIdentity === null) return
    if (ordered.some((camera) => camera.identity === focusedIdentity)) return
    const active = document.activeElement
    if (active !== null && active !== document.body) return
    const neighbour = ordered[Math.min(Math.max(before.findIndex((camera) => camera.identity === focusedIdentity), 0), ordered.length - 1)]
    if (!neighbour) {
      focusInside.current = false
      return
    }
    tilesIn(groupRef.current)
      .find((tile) => tile.dataset.identity === neighbour.identity)
      ?.focus()
  }, [ordered, focusedIdentity])

  if (ordered.length === 0) {
    if (layout !== 'grid') return null
    return <EmptyGrid cameraCount={cameraCount} phase={phase} connection={connection} onCanvas={cameras.length > 0} />
  }

  const tabStop = ordered.some((c) => c.identity === focusedIdentity) ? focusedIdentity : ordered[0].identity
  const compactGrid = layout === 'grid' && compact

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const tiles = tilesIn(event.currentTarget)
    const index = tiles.findIndex((tile) => tile === event.target)
    if (index < 0) return
    const next = keyTarget(event.key, index, tiles.length, layout === 'grid' ? gridRows(ordered, compactGrid) : null)
    if (next === null) return
    event.preventDefault()
    tiles[next]?.focus()
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) focusInside.current = false
  }

  return (
    <div
      ref={groupRef}
      role="group"
      aria-label="Cameras"
      className={cn('watch-strip', className)}
      data-layout={layout}
      data-compact={compactGrid || undefined}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        focusInside.current = true
      }}
      onBlur={handleBlur}
    >
      {ordered.map((camera) => {
        const called = camera.identity === calledIdentity
        const state = tileState(camera, connection)
        const live = state === 'live'
        const name = [camera.name, TILE_WORDS[state], called ? "Host's pick" : null].filter(Boolean).join(', ')
        return (
          <button
            key={camera.identity}
            type="button"
            className="watch-tile"
            data-identity={camera.identity}
            data-source-role={camera.sourceRole}
            data-state={state}
            data-returned={camera.identity === returned || undefined}
            aria-label={name}
            // Without a picture it stays focusable and announced; activating it does nothing.
            aria-disabled={live ? undefined : true}
            tabIndex={camera.identity === tabStop ? 0 : -1}
            onFocus={() => setFocusedIdentity(camera.identity)}
            onClick={() => {
              if (live) onSelect(camera.identity)
            }}
          >
            <span className="watch-tile__thumb">
              {live ? (
                <LiveTrack identity={camera.identity} className="watch-tile__video" fallback={<TileGlyph />} />
              ) : (
                <TileGlyph waiting={state === 'waiting'} />
              )}
              {(live || state === 'offline') && (
                <span className="watch-tile__state">
                  {live ? <Badge tone="live">Live</Badge> : <Badge tone="quiet">Offline</Badge>}
                </span>
              )}
              {called && <HostAvatar host={host} size="xs" className="watch-host-mark watch-tile__host" />}
            </span>
            <span className="watch-tile__caption">
              <SourceGlyph role={camera.sourceRole} />
              <span className="watch-tile__name">{camera.name}</span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

function tilesIn(group: HTMLElement | null): HTMLButtonElement[] {
  return group ? Array.from(group.querySelectorAll<HTMLButtonElement>('[data-identity]')) : []
}

/** A tile without a picture: the crossed-out camera, or the camera, still, while one is on its way. */
function TileGlyph({ waiting = false }: { waiting?: boolean }) {
  return (
    <span className="watch-tile__off" aria-hidden="true">
      {waiting ? <Video /> : <VideoOff />}
    </span>
  )
}

/**
 * The phone's grid with nothing to list: the canvas's glyph on the stacked disc, still (the canvas above carries the
 * motion, the mark and the heading), its words visually hidden. Connected: the camera, for the only camera being on
 * the canvas (`onCanvas`), for no cameras here and for none live (yet, right now, or past the scheduled end). Trying:
 * the signal. Stopped: the crossed-out camera in the neutral disc, no words (the page's notice says why).
 */
function EmptyGrid({ cameraCount, phase, connection, onCanvas }: { cameraCount: CameraCount; phase: EventPhase; connection: CanvasConnection; onCanvas: boolean }) {
  const { art, words } = emptyGridState(cameraCount, phase, connection, onCanvas)
  return (
    <div className="watch-strip-empty">
      {art}
      {words && <span className="sr-only">{words}</span>}
    </div>
  )
}

function emptyGridState(cameraCount: CameraCount, phase: EventPhase, connection: CanvasConnection, onCanvas: boolean): { art: ReactElement; words: string | null } {
  if (connection === 'stopped') return { art: <StateGlyph icon={<VideoOff />} />, words: null }
  if (connection !== 'connected') return { art: <StackedGlyph icon={<Wifi />} />, words: connectionWait(connection).words }
  if (onCanvas) return { art: <StackedGlyph icon={<Video />} />, words: ONLY_ON_CANVAS }
  return { art: <StackedGlyph icon={<Video />} />, words: quietWords(quietCanvas(cameraCount, phase)) }
}

/** The grid's words when the canvas has the only camera it could list. */
const ONLY_ON_CANVAS = 'No other camera to watch'

/**
 * A source that fills a row of the phone's grid (watch.css): the Live show, and a host's shared screen. In the compact
 * grid nothing does.
 */
const wide = (camera: WatchCamera, compact: boolean) => !compact && camera.sourceRole !== 'camera'

/**
 * The grid's rows as tile indexes: a Live show or a host's screen fills a row; cameras pair up (in the compact grid
 * every source pairs up). A wide one after an odd camera (an offline Live show after the live cameras) starts a new
 * row, as the CSS grid places it.
 */
function gridRows(cameras: WatchCamera[], compact: boolean): number[][] {
  const rows: number[][] = []
  cameras.forEach((camera, index) => {
    const last = rows[rows.length - 1]
    const pairable = last && last.length === 1 && !wide(cameras[last[0]], compact)
    if (!wide(camera, compact) && pairable) last.push(index)
    else rows.push([index])
  })
  return rows
}

/** Where a key moves focus from tile `index` of `count`, or null when it doesn't. Up and Down only in the grid. */
function keyTarget(key: string, index: number, count: number, rows: number[][] | null): number | null {
  switch (key) {
    case 'ArrowLeft':
      return index > 0 ? index - 1 : null
    case 'ArrowRight':
      return index < count - 1 ? index + 1 : null
    case 'Home':
      return 0
    case 'End':
      return count - 1
    case 'ArrowUp':
    case 'ArrowDown': {
      if (!rows) return null
      const row = rows.findIndex((r) => r.includes(index))
      const target = rows[row + (key === 'ArrowDown' ? 1 : -1)]
      if (!target) return null
      return target[Math.min(rows[row].indexOf(index), target.length - 1)]
    }
    default:
      return null
  }
}
